import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BundleSectionsTreeNode } from '@/api';
import { BundleType, BundleTypeKeys, type RootState } from '@/store';
import { BUNDLE_PAGE_SCALE_MIN_VALUE, BUNDLE_PAGE_SCALE_STEP, PAGE_SCALE_MAX_VALUE } from '@/constants';

interface BundleSectionsState {
  bundlePageIds: string[];
  bundleSections: BundleSectionsTreeNode[];
  bundleViewedPageIds: string[];
  bundleSelectedThumbnailIndexes: number[];
  bundleCutOutThumbnailIndexes: number[];
  bundlePageScale: number;
}

const initialState: BundleSectionsState = {
  bundlePageIds: [],
  bundleSections: [],
  bundleViewedPageIds: [],
  bundleSelectedThumbnailIndexes: [],
  bundleCutOutThumbnailIndexes: [],
  bundlePageScale: 1.25,
};

const bundleSectionsSlice = createSlice({
  name: 'bundleSections',
  initialState: {
    [BundleType.LIGHT]: initialState,
    [BundleType.FULL]: initialState,
  } as Record<BundleTypeKeys, BundleSectionsState>,
  reducers: {
    setBundlePageIds: (state, action: PayloadAction<{ key: BundleTypeKeys; data: string[] }>) => {
      const { key, data } = action.payload;
      state[key].bundlePageIds = data;
    },
    setBundleSections: (
      state,
      action: PayloadAction<{
        key: BundleTypeKeys;
        data: BundleSectionsTreeNode[];
      }>,
    ) => {
      const { key, data } = action.payload;
      state[key].bundleSections = data;
    },
    setBundleViewedPageIds: (state, action: PayloadAction<{ key: BundleTypeKeys; data: string[] }>) => {
      const { key, data } = action.payload;
      state[key].bundleViewedPageIds = data;
    },
    setBundleSelectedThumbnailIndexes: (state, action: PayloadAction<{ key: BundleTypeKeys; data: number[] }>) => {
      const { key, data } = action.payload;
      state[key].bundleSelectedThumbnailIndexes = data;
    },
    setBundleCutOutThumbnailIndexes: (state, action: PayloadAction<{ key: BundleTypeKeys; data: number[] }>) => {
      const { key, data } = action.payload;
      state[key].bundleCutOutThumbnailIndexes = data;
    },
    setBundlePageScaleZoomIn: (state, action: PayloadAction<{ key: BundleTypeKeys }>) => {
      const { key } = action.payload;
      const newValue = Math.min(state[key].bundlePageScale + BUNDLE_PAGE_SCALE_STEP, PAGE_SCALE_MAX_VALUE);
      state[key].bundlePageScale = newValue;
    },
    setBundlePageScaleZoomOut: (state, action: PayloadAction<{ key: BundleTypeKeys }>) => {
      const { key } = action.payload;
      const newValue = Math.max(state[key].bundlePageScale - BUNDLE_PAGE_SCALE_STEP, BUNDLE_PAGE_SCALE_MIN_VALUE);
      state[key].bundlePageScale = newValue;
    },
  },
});

export const {
  setBundlePageIds,
  setBundleSections,
  setBundleViewedPageIds,
  setBundleSelectedThumbnailIndexes,
  setBundleCutOutThumbnailIndexes,
  setBundlePageScaleZoomIn,
  setBundlePageScaleZoomOut,
} = bundleSectionsSlice.actions;

// RTK SELECTORS WITHOUT CALLBACK

export const selectBundlePageIds = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType ? state.bundleSections[activeBundleType].bundlePageIds : initialState.bundlePageIds;
};
export const selectBundleSections = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType ? state.bundleSections[activeBundleType].bundleSections : initialState.bundleSections;
};
export const selectBundleViewedPageIds = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleSections[activeBundleType].bundleViewedPageIds
    : initialState.bundleViewedPageIds;
};
/**
 * Memoised Set form of the viewed page IDs, for O(1) `.has(id)` checks in hot
 * paths (e.g. computing `isViewed` for every visible thumbnail on each render).
 */
export const selectBundleViewedPageIdsSet = createSelector([selectBundleViewedPageIds], (ids) => new Set(ids));
export const selectBundleSelectedThumbnailIndexes = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleSections[activeBundleType].bundleSelectedThumbnailIndexes
    : initialState.bundleSelectedThumbnailIndexes;
};
export const selectBundleCutOutThumbnailIndexes = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType
    ? state.bundleSections[activeBundleType].bundleCutOutThumbnailIndexes
    : initialState.bundleCutOutThumbnailIndexes;
};
export const selectBundlePageScale = (state: RootState) => {
  const activeBundleType = state.sharedData.activeBundleType;
  return activeBundleType ? state.bundleSections[activeBundleType].bundlePageScale : initialState.bundlePageScale;
};

/** Page IDs for a specific bundle (for cross-bundle page number display) */
export const selectBundlePageIdsForFullBundle = (state: RootState) =>
  state.bundleSections[BundleType.FULL].bundlePageIds;
export const selectBundlePageIdsForLightBundle = (state: RootState) =>
  state.bundleSections[BundleType.LIGHT].bundlePageIds;

// CUSTOM SELECTORS WITH CALLBACK

/**
 * Get pageIndex using pageId
 */
export const makeSelectBundlePageIndexMap = createSelector([selectBundlePageIds], (bundlePageIds) => {
  const indexMap = new Map<string, number>();
  bundlePageIds.forEach((id, idx) => {
    indexMap.set(id, idx);
  });
  return indexMap;
});

export const makeSelectBundlePageIndexByPageId = createSelector([makeSelectBundlePageIndexMap], (indexMap) => {
  return (pageId: string) => indexMap.get(pageId) ?? -1;
});

/**
 * Get pageId using pageIndex
 */
export const makeSelectBundlePageIdByPageIndex = createSelector([selectBundlePageIds], (bundlePageIds) => {
  return (pageIndex: number) => bundlePageIds[pageIndex];
});

/** Page index by pageId for full bundle (for Similar/Duplicate tooltip when viewing light) */
export const makeSelectFullBundlePageIndexByPageId = createSelector(
  [selectBundlePageIdsForFullBundle],
  (bundlePageIds) => (pageId: string) => {
    const idx = bundlePageIds.indexOf(pageId);
    return idx >= 0 ? idx : -1;
  },
);

/** Page index by pageId for light bundle (for Similar/Duplicate tooltip when viewing full) */
export const makeSelectLightBundlePageIndexByPageId = createSelector(
  [selectBundlePageIdsForLightBundle],
  (bundlePageIds) => (pageId: string) => {
    const idx = bundlePageIds.indexOf(pageId);
    return idx >= 0 ? idx : -1;
  },
);

export const bundleSectionsReducer = bundleSectionsSlice.reducer;
